import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowseFileArea } from "./Upload.style";
import Pdf from "../../../../../../assets/images/pdf.png";
import { useController } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import { get, defaultTo, isArray, isEmpty, isNil } from "lodash";
import { IconButton } from "src/components/ui/baseButton";
import { Text } from "src/components/ui/Text";
import { CloseImageIcon } from "../../../../icons";

const Upload = (props) => {
  const {
    name,
    control,
    disabled,
    fullWidth,
    multiple = false,
    multiplePreview = "grid",
    isSubmit = false,
  } = props;

  const imgRef = useRef(null);
  const [files, setFiles] = useState([]);
  //   const [modalOpen, setModalOpen] = useState(false);
  //   const [selectedImage, setSelectedImage] = useState(null);

  const {
    field,
    fieldState: { error },
  } = useController({ control, name });

  const { getInputProps, getRootProps } = useDropzone({
    noClick: true,
    onDrop(acceptedFiles) {
      setFiles([...(field.value ?? []), ...acceptedFiles]);
      return field.onChange([...(field.value ?? []), ...acceptedFiles]);
    },
  });

  const isHasPreview = !isEmpty(files) || !isNil(field.value);

  useEffect(() => {
    // Handle when field.value is undefined, null, or empty
    if (!field.value || field.value?.length === 0) {
      if (isArray(field.value)) {
        setFiles(field.value);
      } else {
        setFiles([]);
      }
    }

    // Handle when field.value has content
    if (field.value?.length > 0) {
      if (isArray(field.value)) {
        setFiles(field.value);
      } else {
        setFiles([field.value]);
      }
    }
  }, [field.value]);

  const filesWithPreview = useMemo(() => {
    try {
      return defaultTo(files, []).map((file) => {
        file.preview =
          file instanceof File ? URL.createObjectURL(file) : file?.preview;
        return file;
      });
    } catch (error) {}
  }, [files]);

  const onUpload = useCallback(() => {
    imgRef.current.click();
  }, [imgRef]);

  const onChange = useCallback(
    (e) => {
      const { onChange } = getInputProps();

      onChange(e);
      if (!multiple) {
        setFiles([e.target?.files[0]]);
        return field.onChange(e.target?.files[0]);
      } else {
        setFiles([...(field.value ?? []), ...e.target?.files]);
        return field.onChange([...(field.value ?? []), ...e.target?.files]);
      }
    },
    [getInputProps, multiple, field]
  );

  const onRemoveFile = useCallback(
    (index) => {
      const cloneFiles = Object.assign(
        [],
        files.filter((item, i) => i !== index)
      );
      if (isEmpty(cloneFiles)) {
        field.onChange(null);
      } else {
        field.onChange(cloneFiles);
      }
      setFiles(cloneFiles);
    },
    [field, files]
  );

  //   const openModal = (image) => {
  //     setSelectedImage(image);
  //     setModalOpen(true);
  //   };

  // Function to handle closing the modal
  //   const closeModal = () => {
  //     setSelectedImage(null);
  //     setModalOpen(false);
  //   };

  return (
    <>
      {multiple && filesWithPreview.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-2 h-full">
          <div {...getRootProps()}>
            <BrowseFileArea
              onUpload={onUpload}
              fullWidth={fullWidth}
              disabled={disabled}
            />
            <input
              id="upload"
              accept="image/png, image/jpeg"
              ref={imgRef}
              hidden
              multiple
              type="file"
              disabled={disabled}
              onChange={onChange}
            />
          </div>

          {/* multiple preview grid or list */}
          <div className={`max-h-[9.5rem] overflow-y-auto pt-2`}>
            {multiplePreview === "grid" ? (
              <div className="flex flex-wrap gap-2 pr-2">
                {filesWithPreview.map((file, i) => {
                  const isPdf = file?.type?.includes("pdf");
                  const hrefForLink = get(file, "preview", "");

                  return (
                    <div
                      className="rounded-[1rem] relative"
                      key={i}
                      style={{ cursor: "pointer" }}
                    >
                      {isPdf ? (
                        <a
                          href={hrefForLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={isPdf ? Pdf : get(file, "preview", "")}
                            alt="uploaded images"
                            className="border rounded-[1rem] w-[6rem] h-[6rem]"
                          />
                          {!isSubmit && !disabled && (
                            <BtnRemoveImage
                              onClick={(e) => {
                                e.preventDefault();
                                onRemoveFile(i);
                              }}
                            />
                          )}
                        </a>
                      ) : (
                        <div className="p-0.5">
                          <img
                            src={isPdf ? Pdf : get(file, "preview", "")}
                            alt="uploaded images"
                            className="border rounded-lg w-[6rem] h-[6rem]"
                            // onClick={() => openModal(file)}
                          />
                          {!isSubmit && !disabled && (
                            <BtnRemoveImage
                              onClick={(e) => {
                                e.preventDefault();
                                onRemoveFile(i);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid gap-2">
                {filesWithPreview.map((file, i) => {
                  const isPdf = file?.type?.includes("pdf");
                  const hrefForLink = get(file, "preview", "");

                  return (
                    <div className=" items-center justify-between" key={i}>
                      <div className="flex justify-between">
                        <div className="flex items-center gap-2">
                          <div className="border min-w-[3rem] w-12 h-12 border-green40 rounded-lg">
                            {isPdf ? (
                              <a
                                href={hrefForLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={isPdf ? Pdf : get(file, "preview", "")}
                                  alt="uploaded images"
                                  className="rounded-lg cursor-pointer w-full h-full object-cover"
                                />
                              </a>
                            ) : (
                              <img
                                src={isPdf ? Pdf : get(file, "preview", "")}
                                alt="uploaded images"
                                className="rounded-lg cursor-pointer w-full h-full object-cover"
                                // onClick={() => openModal(file)}
                              />
                            )}
                          </div>
                          <Text data-theme="main" className="text-primary">
                            {get(file, "name", "-")}
                          </Text>
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          <Text className="text-tertiary whitespace-nowrap">
                            {`${(get(file, "size", 0) / 1024).toFixed(2)} kb`}
                          </Text>
                          {!isSubmit && !disabled && (
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                onRemoveFile(i);
                              }}
                              className="bg-vekin-red100 border-2 border-white hover:border-white hover:bg-danger z-[10]"
                            >
                              <img src={CloseImageIcon} alt="remove_image" />
                            </IconButton>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {isHasPreview &&
          (!isEmpty(get(filesWithPreview, "0.preview")) ||
            !isEmpty(field?.value)) ? (
            <div className="border border-vekin-green40 rounded-lg relative w-full h-full">
              <img
                src={get(filesWithPreview, "0.preview") ?? field?.value}
                alt="uploaded images"
                className="border rounded-lg cursor-pointer w-full h-full object-contain"
              />
              {!isSubmit && !disabled && (
                <BtnRemoveImage
                  onClick={(e) => {
                    e.preventDefault();
                    onRemoveFile(0);
                  }}
                />
              )}
            </div>
          ) : (
            <div {...getRootProps()} className="w-full h-full">
              <BrowseFileArea
                onUpload={onUpload}
                fullWidth={fullWidth}
                disabled={disabled}
              />
              <input
                id="upload"
                accept="image/png, image/jpeg"
                ref={imgRef}
                hidden
                multiple
                type="file"
                disabled={disabled}
                onChange={onChange}
              />
            </div>
          )}
        </>
      )}

      {/* {modalOpen && selectedImage && (
        <Modal
          isOpen={modalOpen}
          onRequestClose={closeModal}
          style={customStyles}
        >
          <button
            className="btn-accent btn-sm fixed top-5 right-5 z-40"
            onClick={closeModal}
          >
            Close
          </button>
          <div className="h-full overflow-auto">
            <img
              src={
                selectedImage?.type?.includes("pdf")
                  ? Pdf
                  : get(selectedImage, "preview", "")
              }
              alt="uploaded images"
              className="rounded"
            />
          </div>
        </Modal>
      )} */}
    </>
  );
};

Upload.displayName = "Upload";

export default Upload;

const BtnRemoveImage = ({ onClick }) => {
  return (
    <IconButton
      onClick={onClick}
      className="absolute -top-[0.625rem] -right-[0.625rem] z-[10] bg-vekin-red100 border-2 border-white hover:border-white hover:bg-danger"
    >
      <img src={CloseImageIcon} alt="remove_image" />
    </IconButton>
  );
};
